import { a as __rest } from '../../../tslib.es6.esm.js';
import { jsx } from 'react/jsx-runtime';
import React__default, { useRef, useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { StickyContainerContext } from '../StickyContainer/StickyContainer.esm.js';
import { TRIGGER_POSITION } from '../constants.esm.js';

// Base component without context
const StickyTriggerBase = React__default.forwardRef((props, ref) => {
    const internalRef = useRef(null);
    // Use useImperativeHandle to expose the ref
    React__default.useImperativeHandle(ref, () => internalRef.current, []);
    const { children = jsx("span", {}), showOnScrollDown, instance = "one", setHasPassedTrigger, setTriggerPosition } = props, rest = __rest(props, ["children", "showOnScrollDown", "instance", "setHasPassedTrigger", "setTriggerPosition"]);
    const { ref: inViewRef, entry } = useInView();
    const [currentPosition, setCurrentPosition] = useState(null);
    // Combine refs - both internal ref and intersection observer ref
    React__default.useEffect(() => {
        if (internalRef.current && inViewRef) {
            inViewRef(internalRef.current);
        }
    }, [inViewRef]);
    // Helper to set has passed trigger state for a specific instance
    const setHasPassedTriggerInstance = (shouldRemain) => {
        setHasPassedTrigger === null || setHasPassedTrigger === void 0 ? void 0 : setHasPassedTrigger({
            [instance]: shouldRemain,
        });
    };
    // Update trigger position in context whenever currentPosition changes
    useEffect(() => {
        if (currentPosition && setTriggerPosition) {
            setTriggerPosition(instance, currentPosition);
        }
    }, [currentPosition, instance, setTriggerPosition]);
    // useEffect to manage hasPassedTrigger for PERSISTENT behavior only
    // OnScroll behavior doesn't need trigger logic
    useEffect(() => {
        // Only set hasPassedTrigger if any action is defined (Persistent behavior)
        const hasAnyAction = showOnScrollDown;
        if (!hasAnyAction) {
            // OnScroll behavior or trigger without config: set hasPassedTrigger = false
            setHasPassedTriggerInstance(false);
            return;
        }
        // Logic for Persistent behavior:
        if (currentPosition === TRIGGER_POSITION.ABOVE) {
            // Already scrolled past trigger → show sticky
            setHasPassedTriggerInstance(true);
        }
        else if (currentPosition === TRIGGER_POSITION.INSIDE ||
            currentPosition === TRIGGER_POSITION.BELOW) {
            // Not past trigger or scrolled back → hide sticky
            setHasPassedTriggerInstance(false);
        }
    }, [currentPosition, showOnScrollDown]);
    useEffect(() => {
        if (entry === null || entry === void 0 ? void 0 : entry.boundingClientRect) {
            if (entry.isIntersecting) {
                setCurrentPosition(TRIGGER_POSITION.INSIDE);
            }
            else if (entry.boundingClientRect.top > 0) {
                setCurrentPosition(TRIGGER_POSITION.BELOW);
            }
            else {
                setCurrentPosition(TRIGGER_POSITION.ABOVE);
            }
        }
    }, [entry]);
    return (jsx("div", Object.assign({ "data-cmp": "stickyTrigger", ref: internalRef }, rest, { children: children })));
});
// Component with context - this will be the main StickyTrigger
const StickyTrigger = React__default.forwardRef((props, ref) => (jsx(StickyContainerContext.Consumer, { children: (context) => context && (jsx(StickyTriggerBase, Object.assign({}, props, { ref: ref, setHasPassedTrigger: context.setHasPassedTrigger, setTriggerPosition: context.setTriggerPosition }))) })));
// Set the display name for better debugging
StickyTrigger.displayName = "StickyTrigger";

export { StickyTrigger };
